import React from 'react';
import TrackableModule from 'components/analytics/TrackableModule';
import RenderedModule from '../RenderedModule/RenderedModule';
import { ModuleList, ModuleTypeName, ModuleTypes, isMappedModule } from 'utils/modules';
import { ContentfulCollection } from 'libs/contentful/types';
import { FooterProps } from 'components/Footer/types';
import { PageSectionProps } from 'components/PageSection/types';
import { SectionHeroProps } from 'components/SectionHero/types';
import { SectionFooterProps } from 'components/SectionFooter/types';
import { SectionNavigationProps } from 'components/SectionNavigation/types';
import { SectionBannerProps } from 'components/SectionBanner/types';
import { DisclosureGroupingsProps } from 'components/DisclosureGroupings/types';
import { SectionDisclosuresProps } from 'components/SectionDisclosures/types';
import { HeroConsolidatedProps } from 'components/HeroConsolidated/types';
import { PageContext } from 'utils/pageContext';
import { PageProps } from 'libs/contentful/queries/page/props';
import PageSection from 'components/PageSection/PageSection';
import SectionHero from 'components/SectionHero/SectionHero';
import SectionDisclosures from 'components/SectionDisclosures/SectionDisclosures';
import HeroConsolidated from 'components/HeroConsolidated/HeroConsolidated';
import Nav from 'components/MainNav/Nav';
import SiteFooter from 'components/Footer/SiteFooter';
import DisclosureGroupings from 'components/DisclosureGroupings/DisclosureGroupings';
import { ContentfulModule } from 'libs/contentful/types';
import { NavProps } from 'components/MainNav/types';
import { NinetailedExperience } from 'components/shared/Ninetailed/NinetailedExperience';
import { getExperiences } from 'utils/serverExperiences';
import { NinetailedMode } from 'types/constants';
import SectionFooter from 'components/SectionFooter/SectionFooter';
import SectionNavigation from 'components/SectionNavigation/SectionNavigation';
import { ResolvedVisualSettings } from '@/utils/pageHelpers/visualSettings';
import SectionBanner from 'components/SectionBanner/SectionBanner';
import { Document, Block, Inline, Text } from '@contentful/rich-text-types';
import { getUseV2Pipeline } from 'libs/experimentation/flags';
import { ModuleRenderer } from 'libs/experimentation/ModuleRenderer';
import { ExposureTracker } from 'libs/experimentation/ExposureTracker';
import {
  injectModuleProps,
  type RenderableModule,
  type RenderableModuleByTypeName,
} from 'libs/experimentation/propInjection';
import { isPersonalizedEntry } from 'libs/experimentation/type-guards';
import { isModuleRegistryTypeName } from 'libs/experimentation/moduleRegistry';

/**
 * Extracts plain text from a Contentful rich text document.
 * Recursively traverses the document tree to collect all text content.
 */
function extractTextFromRichText(document: Document | { json: Document } | undefined): string {
  if (!document) return '';

  // Handle wrapped documents (e.g., { json: Document })
  const doc = 'json' in document ? document.json : document;

  if (!doc?.content) return '';

  const extractText = (nodes: Array<Block | Inline | Text>): string => {
    return nodes
      .map(node => {
        if ('value' in node && typeof node.value === 'string') {
          return node.value;
        }
        if ('content' in node && Array.isArray(node.content)) {
          return extractText(node.content);
        }
        return '';
      })
      .join('');
  };

  return extractText(doc.content).trim();
}

/**
 * Gets the headline text from a module if it has a headline field.
 * Different modules may have the headline in different fields.
 */
function getModuleHeadline(module: ModuleTypes[ModuleTypeName]): string {
  const moduleWithHeadline = module as ModuleTypes[ModuleTypeName] & {
    headline?: Document | { json: Document };
    richTextHeadline?: Document | { json: Document };
  };

  // Try standard headline field first
  if (moduleWithHeadline.headline) {
    return extractTextFromRichText(moduleWithHeadline.headline);
  }

  // Some modules like ImpactHeadline use richTextHeadline
  if (moduleWithHeadline.richTextHeadline) {
    return extractTextFromRichText(moduleWithHeadline.richTextHeadline);
  }

  return '';
}

export interface ModuleCollectionRendererProps<T extends PageProps = PageProps> {
  modulesCollection: ContentfulCollection<ModuleTypes[ModuleTypeName]>;
  allowedModules: ModuleList | undefined;
  headers?: PageContext;
  page?: T;
  ninetailedMode?: NinetailedMode;
  ninetailedExperiment?: boolean;
  visualSettings?: ResolvedVisualSettings;
  enrollmentUrl: string;
}

function getTrackingAttributes(
  module: ModuleTypes[ModuleTypeName],
  moduleIndex: number,
  headers?: PageContext,
  page?: PageProps,
) {
  return {
    moduleTitle: module.sectionName || 'Untitled Section',
    moduleName: getModuleHeadline(module),
    moduleIndex: moduleIndex.toString(),
    moduleLocation: headers?.url || '',
    moduleType: module.__typename,
    pageTitle: page?.title || 'Untitled Page',
    pageTags: page?.contentfulMetadata.tags || [],
  };
}

const isPageSection = (module: ModuleTypes[ModuleTypeName]): module is RenderableModuleByTypeName<'PageSection'> =>
  module.__typename === 'PageSection';

const isRenderableModule = (module: ModuleTypes[ModuleTypeName]): module is RenderableModule =>
  isModuleRegistryTypeName(module.__typename);

function filterModules(
  modules: ModuleTypes[ModuleTypeName][],
  allowedModules: ModuleList | undefined,
): ModuleTypes[ModuleTypeName][] {
  if (!modules) return [];
  return modules.filter(module => {
    if (allowedModules === ModuleList.MAIN && isPageSection(module)) {
      return true;
    }
    return isMappedModule(module, allowedModules);
  });
}

// ---------------------------------------------------------------------------
// V2 Pipeline Renderer — uses moduleRegistry, zero experiment imports
// ---------------------------------------------------------------------------
const PipelineV2Renderer = <T extends PageProps = PageProps>({
  modulesCollection,
  allowedModules,
  headers,
  page,
  visualSettings,
  enrollmentUrl,
}: ModuleCollectionRendererProps<T>) => {
  const MODULE_INDEX_START = 1;

  if (!modulesCollection?.items) {
    return null;
  }

  const modules = filterModules(modulesCollection.items, allowedModules);
  const context = { enrollmentUrl, visualSettings };

  return (
    <>
      {modules.map((module, i) => {
        if (!isRenderableModule(module)) {
          return null;
        }

        const moduleIndex = i + MODULE_INDEX_START;
        const enriched = injectModuleProps(module, context);
        const exposure = isPersonalizedEntry(module) ? module._exposure : undefined;
        const children = isPageSection(module) ? (
          <ModuleCollectionRenderer
            modulesCollection={module.modulesCollection}
            allowedModules={ModuleList.MAIN}
            headers={headers}
            page={page}
            enrollmentUrl={enrollmentUrl}
            visualSettings={visualSettings}
          />
        ) : undefined;

        return (
          <TrackableModule
            key={moduleIndex}
            initialAttributes={getTrackingAttributes(module, moduleIndex, headers, page)}>
            <ExposureTracker exposure={exposure} moduleId={module.sys?.id}>
              <ModuleRenderer module={enriched}>{children}</ModuleRenderer>
            </ExposureTracker>
          </TrackableModule>
        );
      })}
    </>
  );
};

// ---------------------------------------------------------------------------
// Legacy Renderer — existing behavior, zero changes
// ---------------------------------------------------------------------------
const LegacyRenderer = <T extends PageProps = PageProps>({
  modulesCollection,
  allowedModules = undefined,
  headers,
  page,
  ninetailedMode,
  ninetailedExperiment = true,
  visualSettings,
  enrollmentUrl,
}: ModuleCollectionRendererProps<T>) => {
  const MODULE_INDEX_START = 1;

  const NINETAILED_MODULES = {
    PageSection: {
      component: PageSection,
      props: (module: ModuleTypes[ModuleTypeName]) => module as PageSectionProps & ContentfulModule,
    },
    HeroConsolidated: {
      component: HeroConsolidated,
      props: (module: ModuleTypes[ModuleTypeName]) => module as HeroConsolidatedProps & ContentfulModule,
    },
    SectionHero: {
      component: SectionHero,
      props: (module: ModuleTypes[ModuleTypeName]) => module as SectionHeroProps & ContentfulModule,
    },
    SectionDisclosures: {
      component: SectionDisclosures,
      props: (module: ModuleTypes[ModuleTypeName]) => module as SectionDisclosuresProps & ContentfulModule,
    },
    Nav: {
      component: Nav,
      props: (module: ModuleTypes[ModuleTypeName]) => module as NavProps & ContentfulModule,
    },
    SectionNavigation: {
      component: SectionNavigation,
      props: (module: ModuleTypes[ModuleTypeName]) => module as SectionNavigationProps & ContentfulModule,
    },
    SiteFooter: {
      component: SiteFooter,
      props: (module: ModuleTypes[ModuleTypeName]) => module as FooterProps & ContentfulModule,
    },
    SectionFooter: {
      component: SectionFooter,
      props: (module: ModuleTypes[ModuleTypeName]) => module as SectionFooterProps & ContentfulModule,
    },
    SectionBanner: {
      component: SectionBanner,
      props: (module: ModuleTypes[ModuleTypeName]) => module as SectionBannerProps & ContentfulModule,
    },
    DisclosureGroupings: {
      component: DisclosureGroupings,
      props: (module: ModuleTypes[ModuleTypeName]) => module as DisclosureGroupingsProps & ContentfulModule,
    },
  } as const;

  if (!modulesCollection?.items) {
    return null;
  }

  const modules = filterModules(modulesCollection.items, allowedModules);

  const renderModule = (module: ModuleTypes[ModuleTypeName], moduleIndex: number) => {
    const ninetailedModuleConfig = NINETAILED_MODULES[module.__typename as keyof typeof NINETAILED_MODULES];

    // Determine if the current profile has a selected variant for any of this module's experiences
    const profileExperiences = getExperiences();
    const moduleNinetailedData = page?.ninetailedData?.get(module?.sys?.id);
    const hasSelectedExperience = Boolean(
      profileExperiences &&
      moduleNinetailedData?.mappedExperiences?.some(exp => profileExperiences[exp.id] !== undefined),
    );
    if (
      ninetailedExperiment &&
      module.hasExperiments &&
      ninetailedModuleConfig &&
      (hasSelectedExperience || ninetailedMode === 'client')
    ) {
      const ninetailedModuleRenderer = (
        <NinetailedExperience
          key={`section-${module?.sys?.id}`}
          entryId={module?.sys?.id}
          entry={ninetailedModuleConfig.props(module)}
          hasExperiments={module.hasExperiments ?? false}
          ninetailedData={moduleNinetailedData}
        />
      );

      return (
        <TrackableModule
          key={moduleIndex}
          initialAttributes={getTrackingAttributes(module, moduleIndex, headers, page)}>
          {ninetailedModuleRenderer}
        </TrackableModule>
      );
    }

    if (isPageSection(module)) {
      return (
        <TrackableModule
          key={moduleIndex}
          initialAttributes={getTrackingAttributes(module, moduleIndex, headers, page)}>
          <PageSection {...module} enrollmentUrl={enrollmentUrl} />
        </TrackableModule>
      );
    }

    // Inject additional props for specific module types
    const additionalProps: Record<string, unknown> = {};

    // Add showFooterKeyline for footer modules
    if (
      (module.__typename === 'SectionFooter' || module.__typename === 'SiteFooter') &&
      visualSettings?.showFooterKeyline !== undefined
    ) {
      additionalProps.showFooterKeyline = visualSettings?.showFooterKeyline ?? false;
    }

    // Add enrollmentUrl for modules that use it
    if (
      module.__typename === 'HeroConsolidated' ||
      module.__typename === 'HeroCarousel' ||
      module.__typename === 'HomepageHero' ||
      module.__typename === 'ImpactHeadline'
    ) {
      additionalProps.enrollmentUrl = enrollmentUrl;
    }

    const moduleProps = { ...module, ...additionalProps };

    return (
      <TrackableModule key={moduleIndex} initialAttributes={getTrackingAttributes(module, moduleIndex, headers, page)}>
        <RenderedModule
          key={`${module.__typename}-${moduleIndex}`}
          module={module.__typename as ModuleTypeName}
          moduleProps={moduleProps}
        />
      </TrackableModule>
    );
  };

  return <>{modules.map((m, i) => renderModule(m, i + MODULE_INDEX_START))}</>;
};

// ---------------------------------------------------------------------------
// Public component — delegates to v2 or legacy based on feature flag
// ---------------------------------------------------------------------------

/**
 * Renders a collection of modules from an allowed list,
 * wrapping each module with tracking capabilities.
 *
 * @param modulesCollection - The module collection to render
 * @param allowedModules - The modules that are allowed to render
 * @returns The rendered component
 */
const ModuleCollectionRenderer = <T extends PageProps = PageProps>(props: ModuleCollectionRendererProps<T>) => {
  if (getUseV2Pipeline()) {
    return <PipelineV2Renderer {...props} />;
  }
  return <LegacyRenderer {...props} />;
};

export default ModuleCollectionRenderer;
